<template>
  <div
    class="frame"
  >
    <transition
      name="fade"
      mode="out-in"
    >
      <div
        v-if="loading"
        class="frame__loader"
      >
        <ap-loader />
      </div>
      <div
        v-else
        class="frame__inner"
      >
        <div class="container">
          <div class="frame__header">
            <Back 
              :title="serviceInfo?.name"
              :redirect="redirect"
            />
          </div>
          <div class="frame__body">
            <div class="services-form">
              <div class="services-form__inner">
                <div class="services-form__input services-form__tel-number">
                  <ap-input-tel
                    v-model="servicePaymentDetails.phone"
                    :label="t('phone_number')"
                    :icon-name="icon"
                    :error-text="serviceError.phone"
                    @keyup="keyup"
                  />
                </div>
        
                <div class="services-form__transfer-icon">
                  <transfer-icon />
                </div>

                <div class="services-form__input services-form__account-number">
                  <ap-input 
                    v-model="servicePaymentDetails.accountNumber"
                    :label="accountLabel"
                    :error-text="serviceError.identity"
                    :hint="sumHint"
                    @keyup="onKeyUp"
                  />
                </div>

                <div class="services-form__input services-form__sum">
                  <ap-input-money
                    v-model="servicePaymentDetails.sum"
                    :label="amountLabel"
                    :error-text="serviceError.amount"
                    currency
                    :max="limits.max"
                    @keyup="getCommission"
                  />
                </div>

                <div v-if="serviceInfo.additional !== undefined && serviceInfo.additional.length > 0">
                  <div
                    v-for="(field, index) in serviceInfo.additional"
                       :key="index"
                       class="services-form__input services-form__additional"
                  >
                    <ap-input
                      v-model="servicePaymentDetails[field.code]"
                      :label="field.label"
                      :hint="field.hint"
                      :error-text="serviceError[field.code]"
                      @keyup="onKeyUp"
                    />
                  </div>
                </div>

                <div class="services-form__total">
                  <div class="total">
                    <div class="total__item">
                      <div class="total__text">
                        {{ $t('commission') }}
                      </div>
                      <div class="total__number">
                        {{ servicePaymentDetails.commission || 0 }} ₸
                      </div>
                    </div>
                    <div class="total__item">
                      <div class="total__text">
                        {{ $t('total') }}
                      </div>
                      <div class="total__number">
                        {{ formatNumber(servicePaymentDetails.total) || 0 }} ₸
                      </div>
                    </div>
                  </div>
                </div>

                <div class="services-form__btn">
                  <ap-button
                    :full="true"
                    type="primary"
                    :disable="disablePay"
                    @click="payService"
                  >
                    {{$t('pay')}}
                  </ap-button>
                </div>

                <div
                  v-if="serviceError.service_id"
                  class="services-form__error services-form-error"
                >
                  <div class="services-form-error__icon">
                    <img
                      src="@/assets/icons/attentionRed.svg"
                      alt=""
                      srcset=""
                    >
                  </div>
                  <div class="services-form-error__text">
                    {{ serviceError.service_id }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> 
      </div> 
    </transition>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, onBeforeMount, ref, watch } from "vue";
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import debounce from 'lodash/debounce';

import {
  PHONE_LENGTH,
  SERVICES,
  STATUS_APPROVE,
  STATUS_OK
} from '@/constant';

import { formatNumber } from '@/util/helper';

import apButton from '@/components/UI/apButton.vue';
import apInput from '@/components/UI/apInput.vue';
import apInputMask from '@/components/UI/apInputMask.vue';
import apInputMoney from '@/components/UI/apInputMoney.vue';
import apInputTel from '@/components/UI/apInputTel.vue';
import apLoader from '@/components/UI/apLoader.vue';

import Back from '@/components/Back.vue';
import transferIcon from '@/components/icons/transferIcon.vue';

export default defineComponent({
  components: {
    Back,
    apInput,
    apButton,
    apInputTel,
    apLoader,
    transferIcon,
    apInputMask,
    apInputMoney
  },

  setup() {
    const store = useStore();
    const route = useRoute();
    const { t } = useI18n();
    const router = useRouter();

    const servicePaymentDetails = ref({
      phone: '',
      accountNumber: '',
      sum: '',
      total: '',
      commission: '',
    });

    const servicePaymentInfo = ref({});
    
    const loading = ref(false);
    
    const serviceInfo: any = ref({});

    const redirect = computed(() => {
      return {
        name: route.params.fromMain ?  'main' : 'service-list',
        text: route.params.fromMain ? t('main') : serviceInfo.value.catalogs?.[0]?.name,
        ...(!route.params.fromMain && {params: { id: route.params.catalogId || serviceInfo.value.catalogs?.[0]?.id}})
      };
    });
    
    const language = computed(() => store.getters['language']);
    
    const amountLabel = computed(() => `Сумма (от ${serviceInfo.value?.limits.min} ₸ до ${serviceInfo.value?.limits.max} ₸)`);

    const accountLabel = computed(() => serviceInfo.value?.inputs?.[0].name || '');

    const limits = computed(() => {
      return {
        min: serviceInfo.value?.limits.min || null,
        max: serviceInfo.value?.limits.max || null
      };
    });

    const disablePay = computed(() => !(
      servicePaymentDetails.value.phone.length === PHONE_LENGTH &&
      servicePaymentDetails.value.accountNumber.length > 0 &&
      +servicePaymentDetails.value.sum >= serviceInfo.value?.limits.min &&
      +servicePaymentDetails.value.sum <= serviceInfo.value?.limits.max
    ));

    const sumHint = computed(() => serviceInfo?.value?.inputs[0]?.label || '');

    const composeOperatorsText = computed(() => {
      const commissions = serviceInfo.value?.commissions;

      if (!commissions?.length) {
        return '';
      }

      return (
        'Оплата возможна с оператов: ' +
        commissions
          .map((item) =>
            item.operator.charAt(0).toUpperCase() + item.operator.slice(1)
          )
          .join(', ')
      );
    });

    const icon = computed(() => {
      const data = store.getters['bankTransferOperator'];
      const { operator } = data;

      if (operator) {       
        return operator;
      }
      return '';
    });

    const serviceError = computed(() => {
      // Базовые статические поля
      let error = {
        phone: '',
        identity: '',
        amount: '',
        service_id: ''
      }

      // Добавляем динамические поля из serviceInfo
      if (serviceInfo.value?.additional?.length) {
        serviceInfo.value.additional.forEach(field => {
          error[field.code] = '' // Инициализируем каждое поле пустой строкой
        })
      }

      const { payload } = store.getters['services/servicePaymentInfoError']

      if (payload) {
        // Заполняем статические поля
        error.phone = payload?.phone?.[0] || ''
        error.amount = payload?.amount?.[0] || ''
        error.identity = payload?.['inputs?.identity']?.[0] || ''
        error.service_id = payload?.service_id?.[0] || ''

        // Заполняем динамические поля
        if (serviceInfo.value?.additional?.length) {
          serviceInfo.value.additional.forEach(field => {
            error[field.code] = payload?.[`inputs.${field.code}`]?.[0] || ''
          })
        }
      }

      return error
    })

    const clearErrors = () => {
      const error = { ...serviceError.value }

      // Проверяем, есть ли ошибки в любых полях
      const hasErrors = Object.values(error).some(err => err?.length > 0)

      if (hasErrors) {
        store.commit('services/setServicePaymentInfoError', {})
      }
    }

    const getCommission = async ():Promise<void> => {
      const info = serviceInfo.value;
      const details = servicePaymentDetails.value;
      if (details.phone.length === PHONE_LENGTH && details.accountNumber && +details.sum > 0 && details.accountNumber.length) {
        const data = {
          service_id: info.id,
          phone: `7${details.phone}`,
          amount: details.sum,
          inputs: {
            identity: details.accountNumber,
          },
          language: language.value,
        };

        const { commission } = await store.dispatch(
          'services/validateService',
          data
        );
        
        servicePaymentDetails.value.total = commission.total.toFixed(2);
        servicePaymentDetails.value.commission = commission.amount.toFixed(2);
      }
      
      clearErrors();
      
    };


    const onKeyUp = debounce(function() {
      getCommission();
    }, 2500);

    
    const keyup = () => {
      servicePaymentDetails.value.phone.length === PHONE_LENGTH
        ? store.dispatch('checkOperator', { phone: `7${servicePaymentDetails.value.phone}`,
          type: 'card' })
        : store.commit('setOperator', {});
  
      clearErrors();
    };

    const getServiceInfo = async () => {
      loading.value = true;
      store.dispatch('services/getServiceInfo', {
        service_id: route.params.id,
        language: language.value
      }).then(res => {
        const { service } = res;
        serviceInfo.value = service;
      }).finally(() => {
        loading.value = false;
      });
    };
    
    const payService = async () => {
      store.commit('setLoading', true);
      const info = serviceInfo.value;
      const details = servicePaymentDetails.value;
      const inputs = {
        identity: details.accountNumber
      }

      if (info?.additional?.length) {
        info.additional.forEach(field => {
          inputs[field.code] = details[field.code]
        })
      }

      const data = {
        service_id: info.id,
        phone: `7${details.phone}`,
        amount: details.sum,
        inputs: inputs,
        language: language.value,
      };

      const validatedPaymnet = await store.dispatch(
        'services/validateService',
        data
      );

      if (validatedPaymnet && validatedPaymnet.status === STATUS_OK) {
        const paymentInfo = await store.dispatch('services/payService', data);

        if (paymentInfo?.status === STATUS_APPROVE) {
          store.commit('setOtp', {
            phone: details.phone,
            service_id:  paymentInfo.payment.id,
            paymentType: SERVICES,
            otp: ''
          });

          router.push({
            name: 'confirm',
            params: {
              phone: details.phone,
              service_id: paymentInfo.payment.id,
              paymentType: SERVICES,
              otp: '',
            }
          });
        } else {
          router.push({ name: 'service-status',
            params: { phone: details.phone,
              ...(paymentInfo.payment.id && {payment_id: paymentInfo.payment.id}),
              paymentType: SERVICES } });
        }
      }
      store.commit('setLoading', false);
      return;
    };

    const initializeFields = () => {
      serviceInfo.value.additional?.forEach(field => {
        servicePaymentDetails.value[field.code] = ''
        serviceError.value[field.code] = ''
      })
    }

     onBeforeMount(() => {
      store.commit('clearOperator');
      store.commit('services/setServicePaymentInfoError', {});
      getServiceInfo();
    });

     watch(
       () => serviceInfo.value,
       (newValue) => {
         if (newValue?.additional?.length) {
           initializeFields()
         }
       },
       {
         deep: true,
         immediate: true,
       }
     )
    
    return { 
      loading,
      disablePay,
      payService,
      serviceInfo,
      servicePaymentDetails,
      operator: composeOperatorsText,
      keyup,
      icon,
      redirect,
      amountLabel,
      serviceError,
      accountLabel,
      formatNumber,
      clearErrors,
      sumHint,
      limits,
      getCommission,
      onKeyUp,
      t,
    };
  }
});

</script>
<style lang="scss" scoped>
.services-form{
  padding: 50px 0 121px 0;
  &__inner{
    max-width: 350px;
    width: 100%;
    margin: 0 auto;
  }
  &__tel-number{
    margin-bottom: 26px;
  }
  &__transfer-icon{
    margin-bottom: 20px;
    margin: 0 auto;
    width: max-content;
  }
  &__account-number{
    margin-bottom: 12px;
  }
  &__sum{
    margin-bottom: 12px;
  }
  &__additional{
    margin-bottom: 12px;
  }
  &__line{
    margin-bottom: 9px;
  }
  &__total{
    margin-bottom: 24px;
  }
}
.services-form-error{
  display: flex;
  margin-top: 20px;
  &__icon{
    margin-right: 6px;
  }
  &__text{
    color: #B30D14;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@media (min-width: 320px) and (max-width: 480px) {
  .services-form{
    padding: 24px 16px 24px 16px;
  }
}

</style>
